(() => {
  const SELECTOR = '[data-mbb-professionals-audiences]';

  class MbbProfessionalsAudiences {
    constructor(section) {
      if (!section || section.dataset.mbbAudiencesInitialized === 'true') return;

      this.section = section;
      this.viewport = section.querySelector('[data-audiences-viewport]');
      this.track = section.querySelector('[data-audiences-track]');
      this.slides = [...section.querySelectorAll('[data-audiences-slide]')];
      this.prev = section.querySelector('[data-audiences-prev]');
      this.next = section.querySelector('[data-audiences-next]');
      this.pagination = section.querySelector('[data-audiences-pagination]');
      this.navigation = section.querySelector('[data-audiences-navigation]');

      if (!this.viewport || !this.track || !this.slides.length) return;

      this.index = 0;
      this.visible = 6;
      this.maxIndex = 0;
      this.startX = 0;
      this.deltaX = 0;
      this.dragging = false;
      this.resizeTimer = null;

      this.onResize = this.onResize.bind(this);
      this.onKeydown = this.onKeydown.bind(this);
      this.onPointerDown = this.onPointerDown.bind(this);
      this.onPointerMove = this.onPointerMove.bind(this);
      this.onPointerUp = this.onPointerUp.bind(this);

      this.init();
    }

    init() {
      this.section.dataset.mbbAudiencesInitialized = 'true';
      this.viewport.style.touchAction = 'pan-y';

      this.bindEvents();
      this.updateLayout();
    }

    getVisibleSlides() {
      if (window.innerWidth <= 749) return 1;
      if (window.innerWidth <= 1100) return 4;
      return 6;
    }

    getStep() {
      if (this.slides.length < 2) {
        return this.slides[0]?.offsetWidth || 0;
      }

      const first = this.slides[0].getBoundingClientRect();
      const second = this.slides[1].getBoundingClientRect();

      return second.left - first.left;
    }

    updateLayout() {
      this.visible = this.getVisibleSlides();
      this.maxIndex = Math.max(0, this.slides.length - this.visible);
      this.index = Math.min(this.index, this.maxIndex);

      this.buildPagination();
      this.update();
    }

    update() {
      const offset = this.index * this.getStep();

      this.track.style.transform = `translate3d(${-offset}px,0,0)`;

      this.prev?.toggleAttribute('disabled', this.index === 0);
      this.next?.toggleAttribute('disabled', this.index === this.maxIndex);
      if (this.navigation) this.navigation.hidden = this.maxIndex === 0;

      this.slides.forEach((slide, position) => {
        const visible =
          position >= this.index &&
          position < this.index + this.visible;

        slide.setAttribute('aria-hidden', String(!visible));
      });

      this.pagination
        ?.querySelectorAll('button')
        .forEach((dot, position) => {
          const active = position === this.index;

          dot.classList.toggle('is-active', active);
          dot.setAttribute('aria-current', active ? 'true' : 'false');
        });
    }

    goTo(index) {
      const nextIndex = Math.max(0, Math.min(index, this.maxIndex));

      if (nextIndex === this.index) return;

      this.index = nextIndex;
      this.update();
    }

    buildPagination() {
      if (!this.pagination) return;

      this.pagination.replaceChildren();

      for (let index = 0; index <= this.maxIndex; index += 1) {
        const button = document.createElement('button');

        button.type = 'button';
        button.setAttribute(
          'aria-label',
          `Afficher les établissements à partir de la position ${index + 1}`
        );

        button.addEventListener('click', () => this.goTo(index));
        this.pagination.appendChild(button);
      }
    }

    bindEvents() {
      this.prev?.addEventListener('click', () => {
        this.goTo(this.index - 1);
      });

      this.next?.addEventListener('click', () => {
        this.goTo(this.index + 1);
      });

      this.viewport.addEventListener('keydown', this.onKeydown);
      this.viewport.addEventListener('pointerdown', this.onPointerDown);
      this.viewport.addEventListener('pointermove', this.onPointerMove);
      this.viewport.addEventListener('pointerup', this.onPointerUp);
      this.viewport.addEventListener('pointercancel', this.onPointerUp);
      this.viewport.addEventListener('dragstart', event => {
        event.preventDefault();
      });

      window.addEventListener('resize', this.onResize, { passive: true });
    }

    onKeydown(event) {
      if (event.key === 'ArrowLeft') {
        event.preventDefault();
        this.goTo(this.index - 1);
      }

      if (event.key === 'ArrowRight') {
        event.preventDefault();
        this.goTo(this.index + 1);
      }

      if (event.key === 'Home') {
        event.preventDefault();
        this.goTo(0);
      }

      if (event.key === 'End') {
        event.preventDefault();
        this.goTo(this.maxIndex);
      }
    }

    onPointerDown(event) {
      if (event.pointerType === 'mouse' && event.button !== 0) return;

      this.dragging = true;
      this.startX = event.clientX;
      this.deltaX = 0;

      this.viewport.setPointerCapture?.(event.pointerId);
      this.track.style.transition = 'none';
    }

    onPointerMove(event) {
      if (!this.dragging) return;

      this.deltaX = event.clientX - this.startX;

      const baseOffset = this.index * this.getStep();
      const atStart = this.index === 0 && this.deltaX > 0;
      const atEnd = this.index === this.maxIndex && this.deltaX < 0;
      const resistance = atStart || atEnd ? 0.25 : 1;

      this.track.style.transform = `translate3d(${
        -baseOffset + this.deltaX * resistance
      }px,0,0)`;
    }

    onPointerUp(event) {
      if (!this.dragging) return;

      this.dragging = false;
      this.viewport.releasePointerCapture?.(event.pointerId);

      this.track.style.transition =
        'transform .65s cubic-bezier(.22,1,.36,1)';

      const threshold = Math.min(70, this.viewport.offsetWidth * 0.12);

      if (Math.abs(this.deltaX) > threshold) {
        this.goTo(this.index + (this.deltaX < 0 ? 1 : -1));
      } else {
        this.update();
      }

      this.deltaX = 0;
    }

    onResize() {
      clearTimeout(this.resizeTimer);

      this.resizeTimer = setTimeout(() => {
        this.updateLayout();
      }, 120);
    }

    destroy() {
      window.removeEventListener('resize', this.onResize);

      this.viewport.removeEventListener('keydown', this.onKeydown);
      this.viewport.removeEventListener('pointerdown', this.onPointerDown);
      this.viewport.removeEventListener('pointermove', this.onPointerMove);
      this.viewport.removeEventListener('pointerup', this.onPointerUp);
      this.viewport.removeEventListener('pointercancel', this.onPointerUp);

      delete this.section.dataset.mbbAudiencesInitialized;
    }
  }

  const getSections = root => {
    if (root.matches?.(SELECTOR)) return [root];

    return [...(root.querySelectorAll?.(SELECTOR) || [])];
  };

  const init = root => {
    getSections(root).forEach(section => {
      if (section.dataset.mbbAudiencesInitialized === 'true') return;

      section.mbbProfessionalsAudiences =
        new MbbProfessionalsAudiences(section);
    });
  };

  const start = () => init(document);

  document.readyState === 'loading'
    ? document.addEventListener('DOMContentLoaded', start, { once: true })
    : start();

  document.addEventListener('shopify:section:load', event => {
    init(event.target);
  });

  document.addEventListener('shopify:section:unload', event => {
    getSections(event.target).forEach(section => {
      section.mbbProfessionalsAudiences?.destroy();
      delete section.mbbProfessionalsAudiences;
    });
  });
})();
